import React, {useCallback, useEffect, useState} from 'react';
import type {ReactElement} from 'react';
import {useTranslation} from 'react-i18next';

import ThermostatIcon from '@mui/icons-material/Thermostat.js';
import IconButton from '@mui/material/IconButton/IconButton.js';

import type BodyTemperatureModel from '@refinio/one.models/lib/models/BodyTemperatureModel.js';
import type {SHA256Hash} from '@refinio/one.core/lib/util/type-checks.js';

import DefaultAttachmentDetails from '@/root/chat/attachmentViews/defaultView/DefaultAttachmentDetails.js';
import ViewBodyTemperature from '@/root/bodyTemp/view/ViewBodyTemperature.js';

/**
 * @param props.getCertificatePopupView callback to get certificate popup view
 * @param props.onError Optional. Default console.error
 * @returns
 */
export default function BodyTemperatureView(props: {
    hash: SHA256Hash;
    bodyTemperatureModel: BodyTemperatureModel;
    getCertificatePopupView: (onClose: () => void) => ReactElement;
    onError?: (e: Error) => void;
}): ReactElement {
    const i18n = useTranslation();
    const [dataObjectId, setDataObjectId] = useState<string | undefined>(undefined);
    const [isObjectViewOpened, setIsObjectViewOpened] = useState<boolean>(false);

    const toggleObjectView = useCallback(() => {
        setIsObjectViewOpened(oldValue => !oldValue);
    }, []);

    useEffect(() => {
        async function loadDataObjectId(): Promise<void> {
            for await (const dataObject of props.bodyTemperatureModel.bodyTemperaturesIterator()) {
                if (dataObject.dataHash === props.hash) {
                    setDataObjectId(dataObject.id);
                    return;
                }
            }
        }

        if (dataObjectId !== undefined) {
            return;
        }

        loadDataObjectId().catch(props.onError ? props.onError : console.error);
        return props.bodyTemperatureModel.onUpdated(loadDataObjectId);
    }, [props.bodyTemperatureModel]);

    if (!dataObjectId) {
        return <></>;
    }

    return (
        <div className="attachment-box hash-attachment">
            <IconButton
                className="attachment-button"
                sx={{'&:hover': {backgroundColor: 'transparent'}, padding: 0}}
                onClick={toggleObjectView}
            >
                <ThermostatIcon className="icon-avatar-wrapper attachment-icon" />
                <div className="text">{i18n.t('journal.eventTypes.bodyTemperatureTitle')}</div>
            </IconButton>
            <DefaultAttachmentDetails
                getCertificatePopupView={props.getCertificatePopupView}
                onViewObjectRequest={toggleObjectView}
            />
            {isObjectViewOpened && (
                <ViewBodyTemperature
                    bodyTemperatureModel={props.bodyTemperatureModel}
                    id={dataObjectId}
                    onClose={toggleObjectView}
                />
            )}
        </div>
    );
}
